import { z } from 'zod';

// CRA's DefinePlugin only inlines LITERAL `process.env.REACT_APP_*` references at
// build time. We declare a minimal `process` shape here so TS compiles in the
// browser, while keeping the literal access pattern CRA requires.
declare const process: { env: Record<string, string | undefined> };

type RuntimeEnv = Record<string, string | undefined>;

const DEFAULT_BACKEND_URL = 'https://api.commu.click';
const DEFAULT_WEB_URL = 'https://commu.click';
const DEFAULT_WEB_TITLE = 'Communicate POS';
const LOCAL_BACKEND_URL = 'http://localhost:5000';

const EnvSchema = z.object({
    VITE_BACKEND_LOCATION: z.string().url(),
    VITE_WEB_URL: z.string().url(),
    VITE_WEB_TITLE: z.string().min(1),
});

type Env = z.infer<typeof EnvSchema>;

// Summary: Reads Vite env safely without hard dependency on import.meta syntax.
// Details: docs/function-library/web/env-util.md#readmetaenv
const readMetaEnv = (): RuntimeEnv | undefined => {
    try {
        // eslint-disable-next-line no-new-func
        return Function(
            'return (typeof import.meta !== "undefined" && import.meta.env) ? import.meta.env : undefined;',
        )() as RuntimeEnv | undefined;
    } catch {
        return undefined;
    }
};

// Summary: Reads CRA env using literal process.env keys so DefinePlugin can inline.
// Details: docs/function-library/web/env-util.md#readcraenv
const readCraEnv = (): RuntimeEnv => {
    // CRA requires literal `process.env.REACT_APP_*` reads for build-time replacement.
    try {
        return {
            VITE_BACKEND_LOCATION: process.env.REACT_APP_BACKEND_LOCATION,
            VITE_WEB_URL: process.env.REACT_APP_WEB_URL,
            VITE_WEB_TITLE: process.env.REACT_APP_WEB_TITLE,
        };
    } catch {
        return {};
    }
};

// Summary: Detects local browser host for local-backend fallback behavior.
// Details: docs/function-library/web/env-util.md#islocalhosthost
const isLocalHostHost = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

// Summary: Builds raw runtime env object using strict precedence and safe defaults.
// Details: docs/function-library/web/env-util.md#readbuildenv
const readBuildEnv = () => {
    const metaEnv = readMetaEnv();
    const craEnv = readCraEnv();
    const isLocalHost = isLocalHostHost();

    return {
        VITE_BACKEND_LOCATION:
            metaEnv?.VITE_BACKEND_LOCATION ??
            craEnv.VITE_BACKEND_LOCATION ??
            (isLocalHost ? LOCAL_BACKEND_URL : undefined) ??
            DEFAULT_BACKEND_URL,
        VITE_WEB_URL:
            metaEnv?.VITE_WEB_URL ??
            craEnv.VITE_WEB_URL ??
            DEFAULT_WEB_URL,
        VITE_WEB_TITLE:
            metaEnv?.VITE_WEB_TITLE ??
            craEnv.VITE_WEB_TITLE ??
            DEFAULT_WEB_TITLE,
    };
};

// Summary: Validates and returns runtime env config consumed by services and app boot.
// Details: docs/function-library/web/env-util.md#validateenv
export const validateEnv = (): Env => {
    const parsedEnv = EnvSchema.safeParse(readBuildEnv());

    if (!parsedEnv.success) {
        const issues = parsedEnv.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join('; ');
        console.error(parsedEnv.error);
        throw new Error(`Invalid environment variables: ${issues}`);
    }

    return parsedEnv.data;
};
